import { useId, useState } from 'react';
import { LOBBY_CODE_LENGTH, normalizeLobbyCode } from '../../../shared/lobbyCode.ts';

export interface JoinByCodeProps {
  readonly onJoin: (code: string) => void;
  readonly disabled: boolean;
}

/**
 * The keyboard route into a hosted game, for viewers who cannot scan a QR —
 * anyone watching on the same device as the stream, or on a TV.
 *
 * A real `<form>`, so Enter submits and mobile keyboards show a Go key.
 * Normalisation happens on every keystroke rather than on submit, so what the
 * viewer sees is exactly what will be sent.
 */
export function JoinByCode({ onJoin, disabled }: JoinByCodeProps) {
  const [code, setCode] = useState('');
  const inputId = useId();
  const ready = code.length === LOBBY_CODE_LENGTH;

  return (
    <form
      className="joincode"
      onSubmit={(event) => {
        event.preventDefault();
        if (!disabled && ready) onJoin(code);
      }}
    >
      <label className="joincode__label" htmlFor={inputId}>
        Have a code?
      </label>
      <div className="joincode__row">
        <input
          id={inputId}
          className="joincode__input"
          value={code}
          onChange={(event) => setCode(normalizeLobbyCode(event.target.value))}
          placeholder="ABC23"
          maxLength={LOBBY_CODE_LENGTH}
          disabled={disabled}
          // Codes are not words: stop every layer of "helpful" text correction.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="go"
          aria-describedby={`${inputId}-hint`}
        />
        <button type="submit" className="button button--quiet" disabled={disabled || !ready}>
          Join
        </button>
      </div>
      <p className="joincode__hint" id={`${inputId}-hint`}>
        {LOBBY_CODE_LENGTH} characters, from the host&rsquo;s screen.
      </p>
    </form>
  );
}
